import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Trophy, Eye, EyeOff } from 'lucide-react';
import { loginAdminAsync } from '../../store/slices/authSlice';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import { PERSIAN_TEXTS } from '../../constants/persianTexts';
import toast from 'react-hot-toast';

const LoginContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #0f0f23 0%, #1a1a2e 50%, #16213e 100%);
  padding: 20px;
`;

const LoginCard = styled(motion.div)`
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border: 1px solid #2d2d44;
  border-radius: 20px;
  padding: 40px;
  width: 100%;
  max-width: 400px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(10px);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #00d4ff 0%, #4ecdc4 100%);
  }
`;

const Logo = styled.div`
  text-align: center;
  margin-bottom: 30px;
`;

const LogoIcon = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #00d4ff 0%, #4ecdc4 100%);
  border-radius: 50%;
  margin-bottom: 20px;
  box-shadow: 0 10px 30px rgba(0, 212, 255, 0.3);
`;

const Title = styled.h1`
  color: #ffffff;
  font-size: 28px;
  font-weight: 700;
  margin: 0;
  background: linear-gradient(135deg, #00d4ff 0%, #4ecdc4 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Subtitle = styled.p`
  color: #b8b8d1;
  font-size: 16px;
  margin: 10px 0 0 0;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const PasswordContainer = styled.div`
  position: relative;
`;

const PasswordToggle = styled.button`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #6c6c8a;
  cursor: pointer;
  padding: 5px;
  border-radius: 4px;
  
  &:hover {
    color: #00d4ff;
  }
`;

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, error } = useSelector((state) => state.auth);
  
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.username || !formData.password) {
      toast.error('لطفاً تمام فیلدها را پر کنید');
      return;
    }

    try {
      const result = await dispatch(loginAdminAsync(formData)).unwrap();
      toast.success('ورود موفقیت‌آمیز بود');
      navigate('/');
    } catch (error) {
      toast.error(error || 'خطا در ورود');
    }
  };

  return (
    <LoginContainer>
      <LoginCard
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Logo>
          <LogoIcon>
            <Trophy size={40} color="white" />
          </LogoIcon>
          <Title>تورنومنت ادمین</Title>
          <Subtitle>پنل مدیریت تورنومنت بازی</Subtitle>
        </Logo>

        <Form onSubmit={handleSubmit}>
          <Input
            label={PERSIAN_TEXTS['auth.username']}
            name="username"
            value={formData.username}
            onChange={handleChange}
            placeholder="نام کاربری خود را وارد کنید"
            required
          />

          <PasswordContainer>
            <Input
              label={PERSIAN_TEXTS['auth.password']}
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange}
              placeholder="رمز عبور خود را وارد کنید"
              required
            />
            <PasswordToggle
              type="button"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </PasswordToggle>
          </PasswordContainer>

          <Button
            type="submit"
            variant="primary"
            size="large"
            loading={loading}
            style={{ width: '100%', marginTop: '10px' }}
          >
            {PERSIAN_TEXTS['auth.login']}
          </Button>
        </Form>
      </LoginCard>
    </LoginContainer>
  );
};

export default Login;
